import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ReplyIcon from '@mui/icons-material/Reply';
import { useTokens } from 'theme';

export const MAX_QUOTE_CHARS = 1000;

export function referenceAuthor(role) {
  return role === 'user' ? translate('assistant.messageReferenceYou') : translate('assistant.assistantPageAssistant');
}

export function referenceSource(quote) {
  if (typeof quote?.label === 'string') return quote.label;
  const source = quote?.source;
  if (source?.kind === 'step') return translate('assistant.messageReferenceAnalysisProgress');
  if (source?.kind === 'diagnostic_model') return translate('assistant.diagnosticOutputModelMessage');
  if (source?.kind === 'diagnostic_tool') return translate('assistant.diagnosticOutputQueryResult');
  if (source?.kind === 'widget') return translate('assistant.answerWidgetWidgetData2');
  if (source?.kind === 'widget_title') return translate('assistant.answerWidgetWidgetTitle');
  if (source?.kind === 'dashboard') return translate('assistant.dashboardViewDashboard');
  return '';
}

/** A small plain-text reference shared by the draft and the sent message. */
export default function MessageReference({ reference, onCancel, onJump }) {
  const { t: translate } = useTranslation();
  const t = useTokens();
  if (!reference) return null;
  const quote = typeof reference.quote === 'string' ? reference.quote : reference.quote?.text;
  const text = quote || reference.text || translate('assistant.messageReferenceOriginalMessage');
  const Icon = quote ? FormatQuoteIcon : ReplyIcon;
  return (
    <Box
      component="section"
      aria-label={translate('assistant.messageReferenceReplyToMessage')}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, borderLeft: `3px solid ${t.link}`, pl: 1.25, py: 0.5, minWidth: 0 }}
    >
      <Icon aria-hidden="true" sx={{ fontSize: 17, mt: '2px', color: t.link, flexShrink: 0 }} />
      <Box
        component={onJump ? 'button' : 'div'}
        type={onJump ? 'button' : undefined}
        onClick={onJump ? () => onJump(reference.messageId) : undefined}
        aria-label={onJump ? translate('assistant.messageReferenceGoToOriginalMessage') : undefined}
        sx={{ flex: 1, minWidth: 0, p: 0, border: 0, bgcolor: 'transparent', textAlign: 'left', font: 'inherit', cursor: onJump ? 'pointer' : undefined, borderRadius: '2px', '&:focus-visible': { outline: `2px solid ${t.focus}`, outlineOffset: '3px' } }}
      >
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: t.link }}>
          {quote ? translate('assistant.messageReferenceReply') : ''}{referenceAuthor(reference.role)}{referenceSource(reference.quote) ? ` · ${referenceSource(reference.quote)}` : ''}
        </Typography>
        <Typography
          component="div"
          variant="caption"
          sx={{ color: t.inkMuted, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden', lineHeight: 1.5 }}
        >
          {text}
        </Typography>
      </Box>
      {onCancel && (
        <Tooltip title={translate('assistant.messageReferenceCancelReply')}>
          <IconButton size="small" aria-label={translate('assistant.messageReferenceCancelReply')} onClick={onCancel} sx={{ flexShrink: 0, color: t.inkMuted, mt: -0.5 }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
